import { useState, useEffect } from "react";
import {
  Building, TrendingUp, Calendar, FileText, Bell,
  ChevronRight, Download, Filter, Clock, CheckCircle,
  AlertCircle, DollarSign, PieChart, BarChart3,
  Camera, MapPin, Users, Phone, Mail, Shield,
  Eye, ArrowUpRight, Layers, Home, Menu, X
} from "lucide-react";
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";

// ============================================================
// DATA CONFIG - Replace with real data
// ============================================================
const PROJECT = {
  name: "Moldes 3657",
  tagline: "Un diseño contemporáneo para la vida cotidiana",
  developer: "",
  location: "Saavedra, CABA",
  address: "Moldes 3657, Saavedra",
  totalUnits: 15,
  soldUnits: 4,
  totalM2: 800,
  soldM2: 160,
  estimatedROI: "13.5% anual",
  roiTotal: "40% en 3 años",
  completionDate: "Fines de 2028",
  startDate: "Fines de 2026",
  currentPhase: "Preventa",
  floors: 9,
  unitTypes: "2 y 3 ambientes",
  amenities: ["CoWork en PB", "SUM en terraza", "Parrilla", "Laundry", "Terraza con expansión"],
  highlights: [
    { label: "Pisos", value: "9 niveles" },
    { label: "Unidades", value: "2 y 3 amb." },
    { label: "CoWork", value: "51 m² en PB" },
    { label: "Terraza", value: "SUM + Parrilla" },
  ],
};

const PHASES = [
  { name: "Preventa", progress: 20, status: "in-progress", date: "Abr 2026 → durante obra" },
  { name: "Aprobación de Planos", progress: 10, status: "in-progress", date: "Abr – Dic 2026" },
  { name: "Demolición", progress: 0, status: "upcoming", date: "2° semestre 2026" },
  { name: "Construcción", progress: 0, status: "upcoming", date: "Fin 2026 → Fin 2028" },
  { name: "Entrega", progress: 0, status: "upcoming", date: "Fines 2028" },
];

const TIMELINE = [
  { date: "Abril 2026", title: "Inicio de Preventa", description: "Apertura de la comercialización del proyecto con condiciones exclusivas para primeros inversores. Ya se reservaron 4 unidades.", icon: "current", completed: false },
  { date: "Abril – Diciembre 2026", title: "Aprobación de Planos", description: "Presentación del anteproyecto y planos ante el municipio para obtener los permisos de construcción.", icon: "current", completed: false },
  { date: "2° semestre 2026", title: "Demolición", description: "Trabajos de demolición en el terreno para preparación de obra nueva.", icon: "upcoming", completed: false },
  { date: "Fines de 2026", title: "Inicio de Construcción", description: "Arranque de obra: excavación, fundaciones y estructura de hormigón.", icon: "upcoming", completed: false },
  { date: "2027 – 2028", title: "Construcción", description: "Desarrollo completo del edificio: estructura, instalaciones, mampostería, carpintería y terminaciones.", icon: "upcoming", completed: false },
  { date: "Fines de 2028", title: "Entrega de Unidades", description: "Entrega de departamentos a propietarios e inversores con el edificio terminado.", icon: "upcoming", completed: false },
];

const NEWS = [
  { id: 1, category: "Comercial", title: "4 unidades ya reservadas", date: "Mar 2026", summary: "Se concretaron las primeras 4 reservas del proyecto, alcanzando el 20% de los metros vendibles del edificio.", color: "#C9A96E" },
  { id: 2, category: "Proyecto", title: "Lanzamiento oficial de preventa", date: "Mar 2026", summary: "Moldes 3657 abre oficialmente su etapa de preventa con condiciones exclusivas para los primeros inversores.", color: "#4ECDC4" },
  { id: 3, category: "Proyecto", title: "Anteproyecto definitivo presentado", date: "Mar 2026", summary: "Se presentó el anteproyecto: 9 pisos, 15 unidades de 2 y 3 ambientes, CoWork en PB, y amenities en terraza con SUM y parrilla.", color: "#A78BFA" },
  { id: 4, category: "Inversión", title: "Retorno estimado: 40% en 3 años", date: "Mar 2026", summary: "El proyecto ofrece un retorno estimado del 13.5% anual en una zona de alto crecimiento a 2 cuadras de Av. Cabildo.", color: "#C9A96E" },
];

const DOCUMENTS = [
  { id: 1, name: "Anteproyecto Moldes 3657 (PDF completo)", category: "Técnico", date: "Mar 2026", size: "3.0 MB", file: "/anteproyecto-moldes-3657.pdf" },
  { id: 2, name: "Render Fachada Oscura", category: "Comercial", date: "Mar 2026", size: "0.3 MB", file: "/fachada-oscura.jpg" },
  { id: 3, name: "Render Fachada Clara", category: "Comercial", date: "Mar 2026", size: "0.3 MB", file: "/fachada-clara.jpg" },
  { id: 4, name: "Planta Tipo: 2 unidades de 2 amb. (Nivel 1°-7°)", category: "Técnico", date: "Mar 2026", size: "0.2 MB", file: "/planta-2x2amb.jpg" },
  { id: 5, name: "Planta Tipo: 1 unidad de 3 amb. (Nivel 1°-7°)", category: "Técnico", date: "Mar 2026", size: "0.3 MB", file: "/planta-1x3amb.jpg" },
];

const FINANCIAL_DATA = {
  totalInvestment: 45000000,
  spent: 15750000,
  remaining: 29250000,
  distribution: [
    { name: "Terreno", value: 30, color: "#C9A96E" },
    { name: "Construcción", value: 45, color: "#4ECDC4" },
    { name: "Permisos", value: 8, color: "#FF6B6B" },
    { name: "Marketing", value: 7, color: "#A78BFA" },
    { name: "Reserva", value: 10, color: "#60A5FA" },
  ],
  quarterly: [
    { quarter: "Q1 '25", ingresos: 8500, gastos: 3200 },
    { quarter: "Q2 '25", ingresos: 6200, gastos: 4100 },
    { quarter: "Q3 '25", ingresos: 7800, gastos: 3800 },
    { quarter: "Q4 '25", ingresos: 5500, gastos: 4500 },
    { quarter: "Q1 '26", ingresos: 9200, gastos: 3900 },
  ],
};

// ============================================================
// STYLES
// ============================================================
const colors = {
  bg: "#0A0A0F",
  card: "#12121A",
  cardHover: "#1A1A25",
  border: "#1E1E2E",
  accent: "#C9A96E",
  accentLight: "#D4B87A",
  text: "#E8E8EC",
  textSecondary: "#8888A0",
  success: "#4ECDC4",
  warning: "#FFB347",
  danger: "#FF6B6B",
  purple: "#A78BFA",
  blue: "#60A5FA",
};

// ============================================================
// HOOK: detect mobile
// ============================================================
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ============================================================
// COMPONENTS
// ============================================================

function CircularProgress({ progress, size = 200, strokeWidth = 8 }) {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (animatedProgress / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedProgress(progress), 300);
    return () => clearTimeout(timer);
  }, [progress]);

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={colors.border} strokeWidth={strokeWidth} />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.5s ease-in-out" }}
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colors.accent} />
            <stop offset="100%" stopColor={colors.success} />
          </linearGradient>
        </defs>
      </svg>
      <div style={{
        position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
      }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 700, color: colors.text }}>{animatedProgress}%</span>
        <span style={{ fontSize: size * 0.08, color: colors.textSecondary, letterSpacing: 2, textTransform: "uppercase" }}>M² Vendidos</span>
      </div>
    </div>
  );
}

function PhaseBar({ phase, index }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setWidth(phase.progress), 200 + index * 150);
    return () => clearTimeout(timer);
  }, [phase.progress, index]);

  const statusColor = phase.status === "completed" ? colors.success : phase.status === "in-progress" ? colors.accent : colors.textSecondary;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {phase.status === "completed" ? <CheckCircle size={14} color={colors.success} /> :
           phase.status === "in-progress" ? <Clock size={14} color={colors.accent} /> :
           <AlertCircle size={14} color={colors.textSecondary} />}
          <span style={{ color: colors.text, fontSize: 13, fontWeight: 500 }}>{phase.name}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: colors.textSecondary, fontSize: 11 }}>{phase.date}</span>
          <span style={{ color: statusColor, fontSize: 13, fontWeight: 600 }}>{phase.progress}%</span>
        </div>
      </div>
      <div style={{ height: 6, background: colors.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 3, width: `${width}%`,
          background: phase.status === "completed" ? colors.success : `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})`,
          transition: "width 1s ease-in-out",
        }} />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = colors.accent, trend, isMobile }) {
  return (
    <div style={{
      background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
      padding: isMobile ? "16px 14px" : "24px 20px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ color: colors.textSecondary, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
        <div style={{ color: colors.text, fontSize: isMobile ? 17 : 22, fontWeight: 700 }}>{value}</div>
      </div>
      {trend && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: colors.success, fontSize: 11 }}>
          <ArrowUpRight size={12} /> {trend}
        </div>
      )}
    </div>
  );
}

// Mobile bottom nav tab
function BottomNavItem({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
      background: "none", border: "none", cursor: "pointer", padding: "8px 4px", flex: 1,
      color: active ? colors.accent : colors.textSecondary,
      transition: "color 0.2s", minWidth: 0,
    }}>
      <Icon size={20} />
      <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, whiteSpace: "nowrap" }}>{label}</span>
    </button>
  );
}

// Desktop sidebar nav
function NavItem({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
      background: active ? `${colors.accent}15` : "transparent",
      border: "none", borderRadius: 10, cursor: "pointer", width: "100%",
      color: active ? colors.accent : colors.textSecondary,
      transition: "all 0.2s", fontSize: 14, fontWeight: active ? 600 : 400,
      borderLeft: active ? `3px solid ${colors.accent}` : "3px solid transparent",
    }}>
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );
}

// ============================================================
// PAGES
// ============================================================

function DashboardPage({ isMobile, navigateTo }) {
  return (
    <div>
      <div style={{ marginBottom: isMobile ? 20 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Dashboard</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Resumen general del proyecto</p>
      </div>

      {/* Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(200px, 1fr))", gap: isMobile ? 10 : 16, marginBottom: isMobile ? 20 : 32 }}>
        <StatCard icon={Building} label="Departamentos" value={`${PROJECT.totalUnits} unidades`} color={colors.accent} isMobile={isMobile} />
        <StatCard icon={Home} label="Reservadas" value={`${PROJECT.soldUnits} de ${PROJECT.totalUnits}`} color={colors.success} isMobile={isMobile} />
        <StatCard icon={Layers} label="M² Vendidos" value={`${PROJECT.soldM2} / ${PROJECT.totalM2}`} color={colors.blue} trend={`${Math.round(PROJECT.soldM2/PROJECT.totalM2*100)}% del total`} isMobile={isMobile} />
        <StatCard icon={TrendingUp} label="Retorno Estimado" value={PROJECT.estimatedROI} color={colors.purple} trend={PROJECT.roiTotal} isMobile={isMobile} />
      </div>

      {/* Progress + Phases */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 16 : 24, marginBottom: isMobile ? 20 : 32 }}>
        {/* Circular Progress */}
        <div style={{
          background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
          padding: isMobile ? 20 : 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 20
        }}>
          <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: 0, alignSelf: "flex-start" }}>Comercialización</h3>
          <CircularProgress progress={Math.round(PROJECT.soldM2/PROJECT.totalM2*100)} size={isMobile ? 170 : 220} />
          <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: colors.textSecondary, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Inicio Obra</div>
              <div style={{ color: colors.text, fontSize: 13, fontWeight: 600, marginTop: 4 }}>{PROJECT.startDate}</div>
            </div>
            <div style={{ width: 1, background: colors.border }} />
            <div style={{ textAlign: "center" }}>
              <div style={{ color: colors.textSecondary, fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Entrega</div>
              <div style={{ color: colors.text, fontSize: 13, fontWeight: 600, marginTop: 4 }}>{PROJECT.completionDate}</div>
            </div>
          </div>
        </div>

        {/* Phases */}
        <div style={{
          background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: isMobile ? 20 : 32
        }}>
          <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 20px" }}>Fases del Proyecto</h3>
          {PHASES.map((phase, i) => <PhaseBar key={i} phase={phase} index={i} />)}
        </div>
      </div>

      {/* Recent News */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: isMobile ? 16 : 32
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Últimas Actualizaciones</h3>
        <div style={{ display: "grid", gap: 10 }}>
          {NEWS.slice(0, 3).map(item => (
            <div key={item.id} style={{
              padding: isMobile ? 12 : 16, background: colors.bg, borderRadius: 12,
              display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 16,
              border: `1px solid ${colors.border}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: `${item.color}20`, color: item.color, whiteSpace: "nowrap"
                }}>{item.category}</div>
                {isMobile && <span style={{ color: colors.textSecondary, fontSize: 11 }}>{item.date}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.text, fontSize: 13, fontWeight: 500 }}>{item.title}</div>
                <div style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>{item.summary}</div>
              </div>
              {!isMobile && <div style={{ color: colors.textSecondary, fontSize: 12, whiteSpace: "nowrap" }}>{item.date}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <button
        onClick={() => navigateTo("contacto")}
        style={{
          width: "100%", padding: "16px", borderRadius: 14, border: "none", cursor: "pointer",
          background: `linear-gradient(135deg, ${colors.accent}, #E8C547)`, color: "#000",
          fontSize: 16, fontWeight: 700, marginTop: isMobile ? 16 : 24,
          boxShadow: `0 4px 20px ${colors.accent}40`,
        }}
      >
        Quiero invertir →
      </button>
    </div>
  );
}

function TimelinePage({ isMobile }) {
  return (
    <div>
      <div style={{ marginBottom: isMobile ? 20 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Cronograma</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Hitos y progreso del proyecto</p>
      </div>

      <div style={{ position: "relative", paddingLeft: isMobile ? 30 : 40 }}>
        <div style={{
          position: "absolute", left: isMobile ? 10 : 15, top: 0, bottom: 0, width: 2,
          background: `linear-gradient(to bottom, ${colors.accent}, ${colors.border})`
        }} />

        {TIMELINE.map((item, i) => {
          const isCompleted = item.completed;
          const isCurrent = item.icon === "current";
          const dotColor = isCompleted ? colors.success : isCurrent ? colors.accent : colors.textSecondary;

          return (
            <div key={i} style={{ position: "relative", marginBottom: isMobile ? 20 : 32 }}>
              <div style={{
                position: "absolute", left: isMobile ? -25 : -33, top: 8,
                width: isCurrent ? 16 : 12, height: isCurrent ? 16 : 12,
                borderRadius: "50%", background: dotColor,
                border: isCurrent ? `3px solid ${colors.accent}40` : "none",
                boxShadow: isCurrent ? `0 0 16px ${colors.accent}40` : "none",
                marginLeft: isCurrent ? -2 : 0, marginTop: isCurrent ? -2 : 0,
              }} />

              <div style={{
                background: colors.card, border: `1px solid ${isCurrent ? colors.accent + "40" : colors.border}`,
                borderRadius: 14, padding: isMobile ? 16 : 24,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: dotColor, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                      {item.date}
                    </div>
                    <div style={{ color: colors.text, fontSize: isMobile ? 15 : 18, fontWeight: 600, marginBottom: 6 }}>{item.title}</div>
                    <div style={{ color: colors.textSecondary, fontSize: 13, lineHeight: 1.5 }}>{item.description}</div>
                  </div>
                  {isCompleted && <CheckCircle size={20} color={colors.success} />}
                  {isCurrent && (
                    <div style={{
                      padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: `${colors.accent}20`, color: colors.accent, whiteSpace: "nowrap"
                    }}>EN CURSO</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewsPage({ isMobile }) {
  const [filter, setFilter] = useState("Todos");
  const categories = ["Todos", "Comercial", "Proyecto", "Inversión"];
  const filtered = filter === "Todos" ? NEWS : NEWS.filter(n => n.category === filter);

  return (
    <div>
      <div style={{ marginBottom: isMobile ? 16 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Noticias</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Mantente al día con el proyecto</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{
            padding: "7px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
            background: filter === cat ? colors.accent : colors.card,
            color: filter === cat ? colors.bg : colors.textSecondary,
          }}>{cat}</button>
        ))}
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {filtered.map(item => (
          <div key={item.id} style={{
            background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
            padding: isMobile ? 16 : 24,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{
                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: `${item.color}20`, color: item.color
              }}>{item.category}</div>
              <span style={{ color: colors.textSecondary, fontSize: 11 }}>{item.date}</span>
            </div>
            <h3 style={{ color: colors.text, fontSize: isMobile ? 15 : 18, fontWeight: 600, margin: "0 0 6px" }}>{item.title}</h3>
            <p style={{ color: colors.textSecondary, fontSize: 13, margin: 0, lineHeight: 1.6 }}>{item.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentsPage({ isMobile }) {
  const [filter, setFilter] = useState("Todos");
  const categories = ["Todos", "Técnico", "Comercial"];
  const filtered = filter === "Todos" ? DOCUMENTS : DOCUMENTS.filter(d => d.category === filter);

  const handleDownload = (doc) => {
    const link = document.createElement("a");
    link.href = doc.file;
    link.download = doc.name;
    link.click();
  };

  return (
    <div>
      <div style={{ marginBottom: isMobile ? 16 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Documentos</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Biblioteca de documentos</p>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{
            padding: "7px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
            background: filter === cat ? colors.accent : colors.card,
            color: filter === cat ? colors.bg : colors.textSecondary,
          }}>{cat}</button>
        ))}
      </div>

      {/* Mobile: card layout / Desktop: table */}
      {isMobile ? (
        <div style={{ display: "grid", gap: 10 }}>
          {filtered.map(doc => (
            <div key={doc.id} onClick={() => handleDownload(doc)} style={{
              background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 16,
              cursor: "pointer",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <FileText size={20} color={colors.accent} style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.text, fontSize: 14, fontWeight: 500, marginBottom: 6 }}>{doc.name}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 5, fontSize: 10, fontWeight: 500,
                      background: `${colors.accent}15`, color: colors.accent
                    }}>{doc.category}</span>
                    <span style={{ color: colors.textSecondary, fontSize: 11 }}>{doc.date}</span>
                    <span style={{ color: colors.textSecondary, fontSize: 11 }}>{doc.size}</span>
                  </div>
                </div>
                <Download size={16} color={colors.accent} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 120px 120px 100px 50px",
            padding: "14px 24px", borderBottom: `1px solid ${colors.border}`, background: `${colors.accent}08`
          }}>
            {["Documento", "Categoría", "Fecha", "Tamaño", ""].map((h, i) => (
              <span key={i} style={{ color: colors.textSecondary, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{h}</span>
            ))}
          </div>
          {filtered.map((doc, i) => (
            <div key={doc.id} onClick={() => handleDownload(doc)} style={{
              display: "grid", gridTemplateColumns: "1fr 120px 120px 100px 50px",
              padding: "16px 24px", borderBottom: i < filtered.length - 1 ? `1px solid ${colors.border}` : "none",
              alignItems: "center", cursor: "pointer", transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = colors.cardHover}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <FileText size={18} color={colors.accent} />
                <span style={{ color: colors.text, fontSize: 14, fontWeight: 500 }}>{doc.name}</span>
              </div>
              <span style={{
                padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 500,
                background: `${colors.accent}15`, color: colors.accent, justifySelf: "start"
              }}>{doc.category}</span>
              <span style={{ color: colors.textSecondary, fontSize: 13 }}>{doc.date}</span>
              <span style={{ color: colors.textSecondary, fontSize: 13 }}>{doc.size}</span>
              <Download size={16} color={colors.accent} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FinancialPage({ isMobile }) {
  const m2Pct = Math.round(PROJECT.soldM2 / PROJECT.totalM2 * 100);
  const unitPct = Math.round(PROJECT.soldUnits / PROJECT.totalUnits * 100);

  const salesData = [
    { name: "M² Vendidos", value: PROJECT.soldM2, color: colors.accent },
    { name: "M² Disponibles", value: PROJECT.totalM2 - PROJECT.soldM2, color: colors.border },
  ];

  const unitsData = [
    { name: "Reservadas", value: PROJECT.soldUnits, color: colors.success },
    { name: "Disponibles", value: PROJECT.totalUnits - PROJECT.soldUnits, color: colors.border },
  ];

  return (
    <div>
      <div style={{ marginBottom: isMobile ? 16 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Inversión</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Información para inversores</p>
      </div>

      {/* ROI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 10 : 16, marginBottom: isMobile ? 20 : 32 }}>
        <StatCard icon={TrendingUp} label="Retorno Anual" value="13.5%" color={colors.accent} isMobile={isMobile} />
        <StatCard icon={DollarSign} label="Ganancia Total" value="40% en 3 años" color={colors.success} isMobile={isMobile} />
        <StatCard icon={Calendar} label="Plazo de Inversión" value="3 años" color={colors.blue} trend="Entrega fines 2028" isMobile={isMobile} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 16 : 24, marginBottom: isMobile ? 16 : 32 }}>
        {/* M2 Pie Chart */}
        <div style={{
          background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: isMobile ? 20 : 32
        }}>
          <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 20px" }}>Metros Vendidos</h3>
          <div style={{ height: isMobile ? 200 : 250 }}>
            <ResponsiveContainer>
              <RechartsPie>
                <Pie data={salesData} cx="50%" cy="50%" innerRadius={isMobile ? 45 : 60} outerRadius={isMobile ? 75 : 100} paddingAngle={3} dataKey="value">
                  {salesData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text }} formatter={(value) => [`${value} m²`, ""]} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <span style={{ color: colors.accent, fontSize: 24, fontWeight: 700 }}>{m2Pct}%</span>
            <span style={{ color: colors.textSecondary, fontSize: 13, marginLeft: 8 }}>vendido ({PROJECT.soldM2} de {PROJECT.totalM2} m²)</span>
          </div>
        </div>

        {/* Units Pie Chart */}
        <div style={{
          background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: isMobile ? 20 : 32
        }}>
          <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 20px" }}>Unidades Reservadas</h3>
          <div style={{ height: isMobile ? 200 : 250 }}>
            <ResponsiveContainer>
              <RechartsPie>
                <Pie data={unitsData} cx="50%" cy="50%" innerRadius={isMobile ? 45 : 60} outerRadius={isMobile ? 75 : 100} paddingAngle={3} dataKey="value">
                  {unitsData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text }} formatter={(value) => [`${value} unidades`, ""]} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <span style={{ color: colors.success, fontSize: 24, fontWeight: 700 }}>{unitPct}%</span>
            <span style={{ color: colors.textSecondary, fontSize: 13, marginLeft: 8 }}>reservado ({PROJECT.soldUnits} de {PROJECT.totalUnits} deptos)</span>
          </div>
        </div>
      </div>

      {/* Progress bars */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: isMobile ? 16 : 32
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 20px" }}>Avance de Comercialización</h3>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ color: colors.text, fontSize: 13 }}>Metros cuadrados</span>
            <span style={{ color: colors.accent, fontSize: 13, fontWeight: 600 }}>{m2Pct}%</span>
          </div>
          <div style={{ height: 10, background: colors.border, borderRadius: 5, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 5, width: `${m2Pct}%`, background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight})` }} />
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ color: colors.text, fontSize: 13 }}>Unidades</span>
            <span style={{ color: colors.success, fontSize: 13, fontWeight: 600 }}>{unitPct}%</span>
          </div>
          <div style={{ height: 10, background: colors.border, borderRadius: 5, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 5, width: `${unitPct}%`, background: `linear-gradient(90deg, ${colors.success}, #6EE7B7)` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactPage({ isMobile, navigateTo }) {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Push form submit event to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "form_submit",
        form_name: "contacto_inversor",
        form_data: { nombre: form.nombre, email: form.email, telefono: form.telefono }
      });
    }
    setSent(true);
  };

  return (
    <div>
      <div style={{ marginBottom: isMobile ? 20 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>Contacto</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>Dejanos tus datos y te contactamos</p>
      </div>

      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
        padding: isMobile ? 20 : 32, maxWidth: 500,
      }}>
        {sent ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <CheckCircle size={48} color={colors.success} style={{ marginBottom: 16 }} />
            <h3 style={{ color: colors.text, fontSize: 18, fontWeight: 600, margin: "0 0 8px" }}>¡Gracias por tu interés!</h3>
            <p style={{ color: colors.textSecondary, fontSize: 14, margin: "0 0 20px" }}>Nos pondremos en contacto a la brevedad.</p>
            <button
              onClick={() => navigateTo("dashboard")}
              style={{
                padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer",
                background: colors.accent, color: "#000", fontSize: 14, fontWeight: 600,
              }}
            >Volver al inicio</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {[
              { key: "nombre", label: "Nombre completo", type: "text", placeholder: "Juan Pérez" },
              { key: "email", label: "Email", type: "email", placeholder: "juan@email.com" },
              { key: "telefono", label: "Teléfono", type: "tel", placeholder: "+54 11 1234-5678" },
            ].map(field => (
              <div key={field.key} style={{ marginBottom: 16 }}>
                <label style={{ color: colors.text, fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
                  {field.label}
                </label>
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  required
                  value={form[field.key]}
                  onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  style={{
                    width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${colors.border}`,
                    background: colors.bg, color: colors.text, fontSize: 14, outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            ))}
            <button type="submit" style={{
              width: "100%", padding: "12px", borderRadius: 10, border: "none", cursor: "pointer",
              background: `linear-gradient(135deg, ${colors.accent}, #E8C547)`, color: "#000",
              fontSize: 15, fontWeight: 700, marginTop: 8,
            }}>
              Quiero más información
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function FacadeShowcase({ isMobile }) {
  return (
    <div style={{
      background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
      padding: isMobile ? 20 : 32, marginBottom: isMobile ? 16 : 24,
    }}>
      <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 6px" }}>¿Qué fachada te gusta más?</h3>
      <p style={{ color: colors.textSecondary, fontSize: 13, margin: "0 0 20px" }}>
        El edificio cuenta con dos opciones de diseño exterior. La definición final se tomará en conjunto con los inversores.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        {/* Oscura */}
        <div style={{
          borderRadius: 14, overflow: "hidden",
          border: `1px solid ${colors.border}`,
        }}>
          <img
            src="/fachada-oscura.jpg"
            alt="Fachada Oscura"
            style={{ width: "100%", height: isMobile ? 220 : 300, objectFit: "cover", display: "block" }}
          />
          <div style={{ padding: "12px 16px", background: colors.bg }}>
            <span style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>Opción A — Fachada Oscura</span>
            <p style={{ color: colors.textSecondary, fontSize: 12, margin: "4px 0 0" }}>Estilo contemporáneo con terminaciones oscuras y detalles de iluminación cálida</p>
          </div>
        </div>

        {/* Clara */}
        <div style={{
          borderRadius: 14, overflow: "hidden",
          border: `1px solid ${colors.border}`,
        }}>
          <img
            src="/fachada-clara.jpg"
            alt="Fachada Clara"
            style={{ width: "100%", height: isMobile ? 220 : 300, objectFit: "cover", display: "block" }}
          />
          <div style={{ padding: "12px 16px", background: colors.bg }}>
            <span style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>Opción B — Fachada Clara</span>
            <p style={{ color: colors.textSecondary, fontSize: 12, margin: "4px 0 0" }}>Diseño luminoso con madera natural y vidrio, líneas modernas</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectPage({ isMobile }) {
  return (
    <div>
      <div style={{ marginBottom: isMobile ? 16 : 32 }}>
        <h2 style={{ color: colors.text, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0 }}>El Proyecto</h2>
        <p style={{ color: colors.textSecondary, margin: "4px 0 0", fontSize: 13 }}>{PROJECT.tagline}</p>
      </div>

      {/* Building Highlights */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 10 : 16, marginBottom: isMobile ? 20 : 32 }}>
        {PROJECT.highlights.map((h, i) => (
          <div key={i} style={{
            background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
            padding: isMobile ? "16px 14px" : "24px 20px", textAlign: "center",
          }}>
            <div style={{ color: colors.accent, fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: 4 }}>{h.value}</div>
            <div style={{ color: colors.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{h.label}</div>
          </div>
        ))}
      </div>

      {/* Description Card */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
        padding: isMobile ? 20 : 32, marginBottom: isMobile ? 16 : 24,
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Sobre el Edificio</h3>
        <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>
          Moldes 3657 es un edificio de viviendas de 9 niveles con 15 unidades funcionales de 2 y 3 ambientes,
          modernas y luminosas, con expansiones al exterior. Ubicado en Saavedra, a solo 2 cuadras de Av. Cabildo
          y 1 cuadra de García del Río, en una zona de alto crecimiento y gran conectividad.
        </p>
        <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
          El edificio incorpora un espacio de CoWork de 51 m² en planta baja, y amenities en la terraza del nivel 9
          con SUM, parrilla, terraza al aire libre y laundry.
        </p>
      </div>

      {/* Amenities */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
        padding: isMobile ? 20 : 32, marginBottom: isMobile ? 16 : 24,
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 20px" }}>Amenities</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          {[
            { icon: Users, name: "CoWork", desc: "51.32 m² en planta baja con espacios de trabajo integrados", accent: colors.accent },
            { icon: Home, name: "SUM", desc: "25.67 m² en nivel 9, salón de usos múltiples en terraza", accent: colors.success },
            { icon: Camera, name: "Parrilla", desc: "7.26 m² con parrilla y espacio al aire libre en terraza", accent: colors.warning },
            { icon: Layers, name: "Laundry", desc: "10.90 m² lavadero común en nivel 9", accent: colors.blue },
            { icon: Eye, name: "Terraza", desc: "17.47 m² terraza con expansión exterior y vistas", accent: colors.purple },
            { icon: MapPin, name: "Hall de Acceso", desc: "36.83 m² hall de diseño premium en planta baja", accent: colors.accent },
          ].map((a, i) => (
            <div key={i} style={{
              padding: 16, background: colors.bg, borderRadius: 12, border: `1px solid ${colors.border}`,
              display: "flex", gap: 14, alignItems: "flex-start",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: `${a.accent}15`, display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <a.icon size={16} color={a.accent} />
              </div>
              <div>
                <div style={{ color: colors.text, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{a.name}</div>
                <div style={{ color: colors.textSecondary, fontSize: 12, lineHeight: 1.5 }}>{a.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Location */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
        padding: isMobile ? 20 : 32, marginBottom: isMobile ? 16 : 24,
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Ubicación</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <MapPin size={18} color={colors.accent} />
          <span style={{ color: colors.text, fontSize: 15, fontWeight: 500 }}>{PROJECT.address}</span>
        </div>
        <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>
          Saavedra es una de las zonas con mayor crecimiento y proyección de Buenos Aires, con nuevos polos
          gastronómicos y comerciales. A 2 cuadras de Av. Cabildo, 1 cuadra de García del Río y próximo a Núñez.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {["2 cdras Av. Cabildo", "1 cdra García del Río", "Próximo a Núñez", "Transporte público"].map((tag, i) => (
            <span key={i} style={{
              padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 500,
              background: `${colors.accent}15`, color: colors.accent,
            }}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Unit Flexibility */}
      <div style={{
        background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16,
        padding: isMobile ? 20 : 32, marginBottom: isMobile ? 16 : 24,
      }}>
        <h3 style={{ color: colors.text, fontSize: 15, fontWeight: 600, margin: "0 0 16px" }}>Flexibilidad de Unidades</h3>
        <p style={{ color: colors.textSecondary, fontSize: 14, lineHeight: 1.7, margin: "0 0 16px" }}>
          En los niveles 1 a 7, cada piso ofrece dos configuraciones posibles según la demanda:
        </p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div style={{
            padding: 20, background: colors.bg, borderRadius: 14, border: `1px solid ${colors.accent}30`,
            textAlign: "center",
          }}>
            <div style={{ color: colors.accent, fontSize: 28, fontWeight: 800, marginBottom: 4 }}>2</div>
            <div style={{ color: colors.text, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Departamentos de 2 amb.</div>
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>Dos unidades por piso, ideales para inversión y renta</div>
          </div>
          <div style={{
            padding: 20, background: colors.bg, borderRadius: 14, border: `1px solid ${colors.success}30`,
            textAlign: "center",
          }}>
            <div style={{ color: colors.success, fontSize: 28, fontWeight: 800, marginBottom: 4 }}>1</div>
            <div style={{ color: colors.text, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Departamento de 3 amb.</div>
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>Una unidad amplia por piso, ideal para familias</div>
          </div>
        </div>
      </div>

      {/* Facade Poll */}
      <FacadeShowcase isMobile={isMobile} />

      {/* Documents section at bottom */}
      <div style={{ marginTop: isMobile ? 20 : 32 }}>
        <h3 style={{ color: colors.text, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Documentación</h3>
        <div style={{ display: "grid", gap: 10 }}>
          {DOCUMENTS.map(doc => (
            <a key={doc.id} href={doc.file} download={doc.name} style={{
              background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 16,
              display: "flex", alignItems: "center", gap: 12, textDecoration: "none", cursor: "pointer",
              transition: "border-color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = colors.accent}
            onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
            >
              <FileText size={18} color={colors.accent} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.text, fontSize: 13, fontWeight: 500 }}>{doc.name}</div>
                <div style={{ color: colors.textSecondary, fontSize: 11, marginTop: 2 }}>{doc.category} · {doc.size}</div>
              </div>
              <Download size={16} color={colors.accent} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
const navItems = [
  { id: "dashboard", icon: Layers, label: "Inicio" },
  { id: "project", icon: Building, label: "Proyecto" },
  { id: "timeline", icon: Calendar, label: "Cronograma" },
  { id: "news", icon: Bell, label: "Noticias" },
  { id: "financial", icon: PieChart, label: "Inversión" },
];

export default function App() {
  const getPageFromHash = () => {
    const hash = window.location.hash.replace("#/", "").replace("#", "");
    const valid = ["dashboard", "project", "timeline", "news", "financial", "contacto"];
    return valid.includes(hash) ? hash : "dashboard";
  };

  const [page, setPage] = useState(getPageFromHash);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  const navigateTo = (p) => {
    setPage(p);
    window.location.hash = "#/" + p;
    // Push dataLayer event for GTM
    if (window.dataLayer) {
      window.dataLayer.push({ event: "page_view", page_path: "/" + p });
    }
  };

  // Listen to hash changes (back/forward browser buttons)
  useEffect(() => {
    const onHash = () => setPage(getPageFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Close sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile]);

  const renderPage = () => {
    const props = { isMobile };
    switch(page) {
      case "dashboard": return <DashboardPage {...props} navigateTo={navigateTo} />;
      case "project": return <ProjectPage {...props} />;
      case "timeline": return <TimelinePage {...props} />;
      case "news": return <NewsPage {...props} />;
      case "financial": return <FinancialPage {...props} />;
      case "contacto": return <ContactPage {...props} navigateTo={navigateTo} />;
      default: return <DashboardPage {...props} />;
    }
  };

  // ---- MOBILE LAYOUT ----
  if (isMobile) {
    return (
      <div style={{
        minHeight: "100vh", background: colors.bg,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: colors.text, display: "flex", flexDirection: "column",
      }}>
        {/* Mobile Header */}
        <header style={{
          height: 56, borderBottom: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 16px", background: colors.card, position: "sticky", top: 0, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Building size={16} color={colors.bg} />
            </div>
            <div>
              <div style={{ color: colors.text, fontSize: 14, fontWeight: 700 }}>{PROJECT.name}</div>
              <div style={{ color: colors.textSecondary, fontSize: 9, letterSpacing: 1 }}>PORTAL INVERSORES</div>
            </div>
          </div>
          <div style={{
            padding: "4px 10px", borderRadius: 16, fontSize: 10, fontWeight: 500,
            background: `${colors.success}15`, color: colors.success,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: colors.success }} />
            Preventa
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: 16, paddingBottom: 80, overflowY: "auto" }}>
          {renderPage()}
        </main>

        {/* Bottom Navigation */}
        <nav style={{
          position: "fixed", bottom: 0, left: 0, right: 0, height: 64,
          background: colors.card, borderTop: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-around",
          paddingBottom: "env(safe-area-inset-bottom, 0px)", zIndex: 100,
        }}>
          {navItems.map(item => (
            <BottomNavItem key={item.id} icon={item.icon} label={item.label} active={page === item.id} onClick={() => navigateTo(item.id)} />
          ))}
        </nav>
      </div>
    );
  }

  // ---- DESKTOP LAYOUT ----
  return (
    <div style={{
      display: "flex", minHeight: "100vh", background: colors.bg,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: colors.text,
    }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? 260 : 0, minHeight: "100vh",
        background: colors.card, borderRight: `1px solid ${colors.border}`,
        transition: "width 0.3s ease", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "28px 20px 32px", borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Building size={20} color={colors.bg} />
            </div>
            <div>
              <div style={{ color: colors.text, fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>{PROJECT.name}</div>
              <div style={{ color: colors.textSecondary, fontSize: 11, letterSpacing: 1, marginTop: 2 }}>PORTAL INVERSORES</div>
            </div>
          </div>
        </div>

        <nav style={{ padding: "20px 12px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          {navItems.map(item => (
            <NavItem key={item.id} icon={item.icon} label={item.label} active={page === item.id} onClick={() => navigateTo(item.id)} />
          ))}
        </nav>

        <div style={{ padding: "16px 20px", borderTop: `1px solid ${colors.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: colors.success, boxShadow: `0 0 8px ${colors.success}60` }} />
            <span style={{ color: colors.textSecondary, fontSize: 12 }}>Proyecto Activo</span>
          </div>
          <div style={{ color: colors.textSecondary, fontSize: 11, marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}>
            <MapPin size={12} /> {PROJECT.location}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <header style={{
          height: 64, borderBottom: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 32px", background: colors.card,
        }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
            background: "none", border: "none", cursor: "pointer", color: colors.textSecondary,
            display: "flex", alignItems: "center", padding: 8,
          }}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500,
              background: `${colors.success}15`, color: colors.success,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: colors.success }} />
              En Preventa
            </div>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 700, color: colors.bg,
            }}>M3</div>
          </div>
        </header>

        <div style={{ flex: 1, padding: 32, overflowY: "auto" }}>
          {renderPage()}
        </div>
      </main>
    </div>
  );
}